import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatisticsFilter from '../StatisticsFilter';
import StatisticsTableComponent from '../StatisticsTable/StatisticsTable.container';
import StatisticsMetricsComponent from '../StatisticsMetrics/StatisticsMetrics.container';
import StatisticsSegmentsComponent from '../StatisticsSegments/StatisticsSegments.container';
import StatisticsChart from '../StatisticsChart/StatisticsChart.container';
import Pagination from '../../../Pagination';
import StatisticsCSV from '../StatisticsCSV/StatisticsCSV.container';
import config from '../../../../config';
import objectHasChanged from '../../../../helpers/objectHasChanged';
import query from '../../../../helpers/query';
import { I18nContext } from 'utils/I18n/context';
import ReportComponent from '../../../../utils/ReportComponent/ReportComponent';

const reportUtils = ReportComponent.utils;

const defDataSort = {
    sort: 'GroupBy',
    order: 'desc'
};

export function StatisticsContent(props) {
    const ctx = useContext(I18nContext) || {};
    const rawLocation = props && props.location ? props.location : {};
    const parsedQuery = query.parse(rawLocation.query || '');

    const getDateFromString = useCallback((date) => {
        if (date) {
            const d = new Date(date);
            return moment().set({ 'year': d.getFullYear(), 'month': d.getMonth(), 'date': d.getDate() });
        }
        return false;
    }, []);

    let startInit = getDateFromString(parsedQuery.startDate);
    startInit = startInit ? startInit.startOf('day') : moment().startOf('day').subtract(config.defaultDatepickerDaysPeriod, 'days');
    let endInit = getDateFromString(parsedQuery.endDate);
    endInit = endInit ? endInit.endOf('day') : moment().endOf('day');

    const [startDate, setStartDate] = useState(startInit);
    const [endDate, setEndDate] = useState(endInit);
    const [bindActions, setBindActions] = useState(parsedQuery.bindActions || config.defaultBindActions);
    const [groupBy, setGroupBy] = useState(parsedQuery.groupBy || config.defaultGroupBy);
    const [metrics, setMetrics] = useState(parsedQuery.metrics || props.metrics || reportUtils.getDefStrMetric());
    const [sort, setSort] = useState(parsedQuery.sort || defDataSort.sort);
    const [order, setOrder] = useState(parsedQuery.order || defDataSort.order);
    const [segments, setSegments] = useState(parsedQuery.segments || props.segments);
    const [page, setPage] = useState(parsedQuery.page || +props.page);
    const [groupByChart, setGroupByChart] = useState('');
    const [totalPages, setTotalPages] = useState(props.totalPages);
    const [loadingData, setLoadingData] = useState(props.loadingData);

    const isStateSearchChanged = useMemo(() => objectHasChanged(['metrics', 'groupByChart', 'page', 'totalPages', 'loadingData'], true, {
        metrics, groupByChart, page, totalPages, loadingData
    }), [metrics, groupByChart, page, totalPages, loadingData]);

    const pushQuery = useCallback((override) => {
        const sDate = override?.startDate || startDate;
        const eDate = override?.endDate || endDate;
        const gBy = override?.groupBy ?? groupBy;
        const bAct = override?.bindActions ?? bindActions;
        const srt = override?.sort ?? sort;
        const ord = override?.order ?? order;
        const mets = override?.metrics ?? metrics;
        const pg = override?.page ?? page;
        const seg = override?.segments ?? segments;
        const search = {
            startDate: sDate.startOf('day').format(config.reportHourFormat),
            endDate: eDate.endOf('day').format(config.reportHourFormat),
            groupBy: gBy || null,
            bindActions: bAct,
            sort: srt,
            order: ord,
            metrics: mets || null,
            page: pg,
            segments: JSON.stringify(seg) || null
        };
        const pathname = window.location.pathname || '';
        if (!/\/statistics\//.test(pathname)) return;
        query.replace(search);
    }, [startDate, endDate, groupBy, bindActions, sort, order, metrics, page, segments]);

    const onSearch = useCallback((typeAction, override) => {
        setGroupByChart('');
        const sDate = override?.startDate || startDate;
        const eDate = override?.endDate || endDate;
        const gBy = override?.groupBy ?? groupBy;
        const bAct = override?.bindActions ?? bindActions;
        const srt = override?.sort ?? sort;
        const ord = override?.order ?? order;
        const mets = override?.metrics ?? metrics;
        const pg = override?.page ?? page;
        const seg = override?.segments ?? segments;
        const statePayload = { startDate: sDate, endDate: eDate, bindActions: bAct, groupBy: gBy, metrics: mets, sort: srt, order: ord, segments: seg, page: pg, groupByChart: '' };
        props.search(statePayload);
        if (typeAction !== 'table' && typeAction !== 'pagination') {
            props.searchChart(statePayload);
        }
    }, [props, startDate, endDate, bindActions, groupBy, metrics, sort, order, segments, page]);

    useEffect(() => {
        onSearch();
        pushQuery();
    }, []);

    useEffect(() => {
        if (props.totalPages !== totalPages || props.loadingData !== loadingData) {
            setTotalPages(props.totalPages);
            setLoadingData(props.loadingData);
        }
    }, [props.totalPages, props.loadingData]);

    const onUpdateDate = useCallback(() => {
        if (loadingData) return;
        const changed = isStateSearchChanged({ metrics, groupByChart, page, totalPages, loadingData });
        const nextPage = changed ? config.initialPage : page;
        setPage(nextPage);
        setSort(defDataSort.sort);
        setOrder(defDataSort.order);
        const override = { page: nextPage, sort: defDataSort.sort, order: defDataSort.order };
        onSearch(undefined, override);
        pushQuery(override);
    }, [loadingData, isStateSearchChanged, metrics, groupByChart, page, totalPages, onSearch, pushQuery]);

    const onActionFilter = useCallback((filterState) => {
        const changed = isStateSearchChanged({ metrics, groupByChart, page, totalPages, loadingData });
        const nextPage = changed ? config.initialPage : page;
        const nextStart = (filterState.startDate !== undefined) ? filterState.startDate : startDate;
        const nextEnd = (filterState.endDate !== undefined) ? filterState.endDate : endDate;
        const nextGroup = (filterState.groupBy !== undefined) ? filterState.groupBy : groupBy;
        const nextBind = (filterState.bindActions !== undefined) ? filterState.bindActions : bindActions;
        setPage(nextPage);
        if (filterState.startDate !== undefined) setStartDate(filterState.startDate);
        if (filterState.endDate !== undefined) setEndDate(filterState.endDate);
        if (filterState.groupBy !== undefined) setGroupBy(filterState.groupBy);
        if (filterState.bindActions !== undefined) setBindActions(filterState.bindActions);
        const override = { startDate: nextStart, endDate: nextEnd, groupBy: nextGroup, bindActions: nextBind, page: nextPage };
        onSearch(undefined, override);
        pushQuery(override);
    }, [isStateSearchChanged, metrics, groupByChart, page, totalPages, loadingData, startDate, endDate, groupBy, bindActions, onSearch, pushQuery]);

    const onActionMetrics = useCallback((m) => {
        const metricsString = Array.isArray(m) ? reportUtils.getStrMetric(m) : m;
        setMetrics(metricsString);
        pushQuery();
    }, [pushQuery]);

    const onActionTable = useCallback((dataSort) => {
        if (typeof dataSort.sort !== 'undefined') setSort(dataSort.sort);
        if (typeof dataSort.order !== 'undefined') setOrder(dataSort.order);
        onSearch('table');
        pushQuery();
    }, [onSearch, pushQuery]);

    const onPageChange = useCallback((p) => {
        setPage(p);
        onSearch('pagination');
        pushQuery();
    }, [onSearch, pushQuery]);

    const onActionSegments = useCallback((s) => {
        setSegments(s);
    }, []);

    const onActionChart = useCallback((state) => {
        setGroupByChart(state.currentGroupBy);
        const payload = { startDate, endDate, bindActions, groupBy, metrics, sort, order, segments, page, groupByChart: state.currentGroupBy };
        props.searchChart(payload);
    }, [props, startDate, endDate, bindActions, groupBy, metrics, sort, order, segments, page]);

    return (
        <section className="wrapper-content statistics">
            <div className="ibox">
                <StatisticsFilter
                    startDate={startDate}
                    endDate={endDate}
                    groupBy={groupBy}
                    bindActions={bindActions}
                    onAction={onActionFilter}
                />
                <StatisticsSegmentsComponent
                    segments={segments}
                    onAction={onActionSegments}
                    onUpdateDate={onUpdateDate}
                    loading={loadingData}
                />
            </div>
            <div className="ibox-title bold" id="statistics-table-header">
                <StatisticsMetricsComponent onAction={onActionMetrics}/>
                <StatisticsCSV/>
            </div>

            <StatisticsChart onAction={onActionChart} />

            <div className="card dashboard-table-card">
                <div className="card-body">
                    <div className="statistics__top-info">
                        <Pagination
                            onPageChange={onPageChange}
                            initialPage={page}
                            total={props.total || 0}
                            loading={loadingData}
                            itemsCountPerPage={config.reportItemsCount}
                            align={'right'}
                            pagesToDisplay={5}
                            showFirstLast={true}
                            showPrevNext={true}
                        />
                    </div>
                    <StatisticsTableComponent
                        sort={sort}
                        order={order}
                        metrics={metrics}
                        onAction={onActionTable}
                    />
                </div>
            </div>
        </section>
    );
}

StatisticsContent.propTypes = {
    metrics: PropTypes.string,
    segments: PropTypes.object
};

StatisticsContent.defaultProps = {
    metrics: '',
    segments: {}
};


export default StatisticsContent;
